/**
 * Dex scan diagnostics (Push 6 / v1.4.2 b2).
 *
 * A tiny, Dex-only diagnostic store. The new "Dex Live Scan" menu and
 * its diagnostic panel read from here so we can show the user concrete,
 * non-fabricated information about the most recent scan attempt:
 *
 *   - what variant the classifier picked
 *   - whether we were looking at a 4×4 in-game grid or a region overview
 *   - how many cells were observed vs how many Pokédex numbers were read
 *   - confidence reported by the OCR pipeline
 *   - how many entries were applied to dexStateStore vs skipped
 *   - timestamp of the last frame so the panel can show "5s ago"
 *
 * The contract is intentionally strict: callers MUST publish only real
 * results from actual OCR runs. Never seed sample data — the user's
 * direction is explicit about not faking scan output. When no scan has
 * happened in this session the panel renders "no scan yet" using the
 * `null` returned by `readDexScanDiagnostics`.
 */
import type {
  PokedexVariant,
  PokedexRegionLite,
} from "./pogoPokedexVariantClassifier";
import type { VariantSignalSource } from "./variantSignals";
import type { TopPanelVariantCounts } from "./topPanelOcr";

export const DEX_SCAN_DIAG_EVENT = "nexus:dex-scan-diag-changed";

export type DexScanMode = "grid_4x4" | "region_overview" | "unknown";

export type DexScanSource = "live" | "upload";

export interface DexScanDiagnostics {
  /** When the scan completed (Date.now). */
  ts: number;
  /** "live" once we wire native, "upload" for the screenshot import path. */
  source: DexScanSource;
  /** Variant detected by the classifier; null when nothing was readable. */
  variant: PokedexVariant | null;
  /** Whether the classifier inferred the variant from the calling page. */
  variantInferred: boolean;
  /**
   * PR-C Problem 1: two-signal (tab-pill OCR + screen tint) variant vote
   * provenance. Lets the LAST SCAN panel show WHY a variant was chosen —
   * e.g. "Lucky (tab-OCR ✓ tint ✓)" / "Main (sticky)" / "unresolved" — so
   * the user can trust (or distrust) the read before any state is committed.
   * Optional so older snapshots and the upload path deserialize unaffected.
   */
  variantConfidence?: number;
  variantSource?: VariantSignalSource;
  variantDetail?: string;
  /** True when the tab-OCR and tint signals disagreed (telemetry). */
  variantDisagreement?: boolean;
  /** Region detected by the classifier (Kanto / Johto / etc.) or "unknown". */
  region: PokedexRegionLite;
  /** Which screen shape the scanner reads — drives downstream assumptions. */
  mode: DexScanMode;
  /** OCR confidence percentage 0-100. */
  confidence: number;
  /** Frames the session has analysed so far (any mode). Optional for older snapshots. */
  framesObserved?: number;
  /** Frames that passed the gate as a 4×4 grid. Optional for older snapshots. */
  framesApplied?: number;
  /**
   * Candidate grid cells accumulated across ACCEPTED frames only, capped at
   * 16 per frame (a 4×4 screen is physically 16 cells). This is never the
   * raw legacy 3×6 sweep total — see dexLiveScanSession.MAX_CELLS_PER_FRAME.
   */
  cellsObserved: number;
  /** Cells where a Pokédex number was successfully extracted. */
  numbersRead: number;
  /** Entries applied to dexStateStore (skips manual overrides). */
  applied: number;
  /** Entries skipped because a manual override already exists. */
  skippedManual: number;
  /** Cells that did not yield a Pokédex number. */
  unknownCells: number;
  /**
   * Push 1 / v1.4.3: per-reason counts of frames/numbers the pre-apply
   * gate or stability rule rejected. Optional so older callers (e.g. the
   * upload path) deserialize without it.
   */
  gated?: DexScanGatedCounts;
  /**
   * Push 3 / v1.4.3 b3: advanced image-pipeline summary (registration
   * classification + crops + structured session JSON). Optional so the
   * legacy upload path and older deserialized snapshots are unaffected.
   */
  imagePipeline?: DexImagePipelineDiag;
  /** Optional human-readable note, e.g. "low confidence" or a warning. */
  note?: string;
  /**
   * Push 5 / v1.4.3 b5: how the region was resolved — "label" (visible
   * header), "tab_text", "tab_column", or "unresolved". Lets the panel show
   * WHY a region was (or was not) applied instead of silently guessing.
   */
  regionReason?: string;
  /**
   * Push 5 / v1.4.3 b5: candidate grid cells on the last frame whose number
   * ROI was masked out because the PiP overlay covered them.
   */
  pipObscuredCells?: number;
  /**
   * Push 8 / v1.4.3 b8: the Pokémon GO top-panel summary read for this scan.
   * Present only when a top panel was parsed. Carries the trusted region +
   * main count + the action the app took (bulk_region_complete / narrow_missing
   * / conflict / ignored_low_confidence). Optional so older snapshots and the
   * live path deserialize without it.
   */
  topPanel?: TopPanelDiagShape;
}

/** Diagnostic projection of the top-panel reading. */
export interface TopPanelDiagShape {
  top_panel_region: string;
  top_panel_main_count: string | null;
  top_panel_missing_expected: number | null;
  top_panel_action: string;
  top_panel_variants?: TopPanelVariantCounts;
  top_panel_confidence: number;
}

/**
 * Push 3 / v1.4.3 b3: the new structured-JSON / registration / crop layer
 * surfaced in the diagnostics panel. Counts are session-cumulative.
 */
export interface DexImagePipelineDiag {
  /** Canonical region tag the session is tagging entries with. */
  region: string;
  /** Distinct entries scanned this session. */
  entriesScanned: number;
  /** Entries classified as registered (vibrant sprite). */
  newRegistrations: number;
  /** Observations skipped because the dex number was already in-session. */
  dedupedSkipped: number;
  /** Frames skipped by the throttle/motion gate. */
  motionSkippedFrames: number;
  /** Cells skipped for not-fully-visible / low confidence. */
  visibilitySkippedCells: number;
  /** Registered cells whose crop was actually persisted. */
  cropsCaptured: number;
  /** Registered cells whose crop could NOT be persisted (honest). */
  cropsUnavailable: number;
  /**
   * The exact session_update JSON the pipeline produced, serialized. Shown
   * in the panel so a tester can copy the structured output verbatim.
   */
  sessionJson?: string;
}

/** Mirror of the session's gated-reason counters, for the diag panel. */
export interface DexScanGatedCounts {
  not_pokedex_grid: number;
  low_confidence: number;
  overlay_frame: number;
  unstable_number: number;
  manual_override: number;
}

/**
 * Reconcile a diagnostic note with the SESSION-CUMULATIVE counters so the
 * panel never contradicts itself (Push 2 / v1.4.4 b3).
 *
 * The bug this fixes: the per-frame adapter would set
 * note = "No Pokédex numbers visible" whenever the LAST frame read zero
 * numbers, even though the cumulative `numbersRead` across the session was
 * large (e.g. 94). The panel showed "Numbers read 94" next to
 * "No Pokédex numbers visible", which is nonsense.
 *
 * Rule: if the session has read ANY numbers, suppress the "no numbers"
 * note and prefer a truthful summary; only show the no-numbers note when
 * the session genuinely has zero reads.
 */
export function resolveDiagnosticNote(args: {
  numbersRead: number;
  applied: number;
  frameNote?: string;
  regionReason?: string;
}): string | undefined {
  const { numbersRead, applied, frameNote, regionReason } = args;
  const NO_NUMBERS = "No Pokédex numbers visible";

  if (numbersRead > 0) {
    // The "no numbers" note is a lie once we've read any. Replace it with a
    // truthful status derived from the cumulative counters.
    if (!frameNote || frameNote === NO_NUMBERS) {
      if (applied > 0) {
        return `${applied} entr${applied === 1 ? "y" : "ies"} applied · ${numbersRead} number${numbersRead === 1 ? "" : "s"} read`;
      }
      // Numbers read but none applied yet — usually a region/variant the
      // scanner couldn't commit. Clarify rather than implying a blank grid.
      return regionReason === "tab_column"
        ? `${numbersRead} numbers read; resolving variant/category from tab`
        : `${numbersRead} numbers read; not applied yet`;
    }
    return frameNote;
  }

  // Genuinely zero numbers this session: the no-numbers note is honest.
  return frameNote ?? NO_NUMBERS;
}

/**
 * Human-readable "Mode" clarification (Push 2 / v1.4.4 b3). When the screen
 * mode is "unknown" but the region was resolved from the tab column, the
 * uncertainty is about the variant/category — NOT the grid layout — so the
 * panel should say so instead of a bare "Unknown" that implies it can't even
 * tell it's looking at the grid.
 */
export function formatModeLabel(
  mode: DexScanMode,
  regionReason?: string,
): string {
  if (mode === "grid_4x4") return "4 × 4 grid (Pokémon GO)";
  if (mode === "region_overview") return "Region overview";
  // mode === "unknown"
  if (regionReason === "tab_column") {
    return "Grid (variant/category unconfirmed)";
  }
  return "Unknown";
}

let latest: DexScanDiagnostics | null = null;

function emitChange(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event(DEX_SCAN_DIAG_EVENT));
  } catch {
    /* swallow */
  }
}

/**
 * Replace the diagnostic snapshot. Callers should always pass real data
 * from an actual scan — pass null to clear (e.g. on menu open if a
 * tester wants to verify "no scan yet" state).
 */
export function publishDexScanDiagnostics(diag: DexScanDiagnostics | null): void {
  latest = diag;
  emitChange();
}

export function readDexScanDiagnostics(): DexScanDiagnostics | null {
  return latest;
}

export function clearDexScanDiagnostics(): void {
  if (latest === null) return;
  latest = null;
  emitChange();
}

/**
 * Format a "Nm ago" / "Ns ago" label for the panel header. Returns
 * null if there is no diagnostic yet.
 */
export function formatLastScanAgo(now: number = Date.now()): string | null {
  if (!latest) return null;
  const delta = Math.max(0, now - latest.ts);
  if (delta < 1500) return "just now";
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  return `${Math.floor(delta / 3_600_000)}h ago`;
}
